import React, { FC } from 'react';
import { CircularProgress, SxProps } from '@mui/material';
import LoadingButton, { loadingButtonClasses, LoadingButtonProps } from '@mui/lab/LoadingButton';


interface SizeProps {
    size?: 'large' | 'medium' | 'small' | 'extraSmall' | 'extraExtraSmall';
}

export interface VariantsProps {
    variantType?: 'main' | 'brand';
    variant?: 'primary' | 'secondary' | 'stroke';
}

const buttonPyForSize: Record<SizeProps['size'], number> = {
    large: 4,
    medium: 3,
    small: 2,
    extraSmall: 1.5,
    extraExtraSmall: 1.5,
};

const buttonPxForSize: Record<SizeProps['size'], number> = {
    large: 7,
    medium: 5,
    small: 5,
    extraSmall: 4.5,
    extraExtraSmall: 4.5,
};

const circularProgressForSize: Record<SizeProps['size'], number> = {
    large: 24,
    medium: 24,
    small: 20,
    extraSmall: 20,
    extraExtraSmall: 16,
};

export const sxPropsByVariantsProps: Record<VariantsProps['variantType'], Record<VariantsProps['variant'], SxProps>> = {
    main: {
        primary: {
            color: 'grayscale.shade0',
            bgcolor: 'grayscale.shade95',

            '&:hover': {
                bgcolor: 'grayscale.shade85',
            },
            '&:focus': {
                bgcolor: 'grayscale.shade90',
            },
            [`&.${loadingButtonClasses.loading}`]: {
                bgcolor: 'general.primary80',
            },
            '&:disabled': {
                color: 'grayscale.shade85',
                bgcolor: 'grayscale.shade40',
            },
        },
        secondary: {
            color: 'grayscale.shade95',
            bgcolor: 'grayscale.shade0',

            '&:hover': {
                bgcolor: 'grayscale.shade20',
            },
            '&:focus': {
                bgcolor: 'grayscale.shade40',
            },
            [`&.${loadingButtonClasses.loading}`]: {
                bgcolor: 'grayscale.shade40',
            },
            '&:disabled': {
                color: 'grayscale.shade85',
                bgcolor: 'grayscale.shade40',
            },
        },
        stroke: {
            color: 'grayscale.shade95',
            bgcolor: 'grayscale.shade10',

            '&:hover': {
                bgcolor: 'grayscale.shade20',
                color: 'grayscale.shade95',
            },
            '&:focus': {
                bgcolor: 'grayscale.shade40',
            },
            '&:visited': {
                color: 'grayscale.shade95',
            },
            [`&.${loadingButtonClasses.loading}`]: {
                bgcolor: 'grayscale.shade40',
            },
            '&:disabled': {
                color: 'grayscale.shade85',
                bgcolor: 'grayscale.shade40',
            },
        },
    },
    brand: {
        primary: {
            color: 'grayscale.shade0',
            bgcolor: 'general.primary50',
            borderColor: 'transparent',
            '&:hover': {
                bgcolor: 'general.primary60',
            },
            '&:focus': {
                bgcolor: 'general.primary80',
            },
            [`&.${loadingButtonClasses.loading}`]: {
                bgcolor: 'general.primary50',
                [`& > .${loadingButtonClasses.loadingIndicator}`]: {
                    color: 'grayscale.shade0',
                },
            },

            [`&:disabled:not(.${loadingButtonClasses.loading})`]: {
                color: 'grayscale.shade85',
                bgcolor: 'grayscale.shade40',
            },
        },
        secondary: {
            color: 'general.primary60',
            bgcolor: 'general.primary10',
            borderColor: 'transparent',
            '&:hover': {
                bgcolor: 'general.primary20',
            },
            '&:focus': {
                bgcolor: 'general.primary30',
            },
            [`&.${loadingButtonClasses.loading}`]: {
                bgcolor: 'general.primary30',
            },
            '&:disabled': {
                color: 'grayscale.shade85',
                bgcolor: 'grayscale.shade40',
            },
        },
        stroke: {
            color: 'general.primary60',
            bgcolor: 'general.primary10',
            borderColor: 'general.primary50',
            '&:hover': {
                bgcolor: 'general.primary20',
            },
            '&:focus': {
                bgcolor: 'general.primary30',
            },
            [`&.${loadingButtonClasses.loading}`]: {
                bgcolor: 'general.primary30',
            },
            '&:disabled': {
                color: 'grayscale.shade85',
                bgcolor: 'grayscale.shade40',
                borderColor: 'transparent',
            },
        },
    },
};

export interface Button2Props extends
    VariantsProps,
    SizeProps,
    Omit<LoadingButtonProps, 'color' | 'size' | 'variant'> { }

export const Button2: FC<Button2Props> = ({
    size = 'medium',
    variantType = 'main',
    variant = 'primary',
    sx,
    ...props
}) => (
    <LoadingButton
        sx={[({ typography }) => ({
            py: buttonPyForSize[size],
            px: buttonPxForSize[size],

            ...typography[size === 'extraExtraSmall' ? 'mediumCaption1' : 'mediumBody2'],

            border: 0,
            borderRadius: 2.5,

            ...sxPropsByVariantsProps[variantType][variant],
        }),
        ...Array.isArray(sx) ? sx : [sx]]}
        loadingIndicator={<CircularProgress color='inherit' size={circularProgressForSize[size]} />}
        {...props}
    />
);
